import { useEffect, useState, useRef } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { StatusBadge, fmtDate } from "../helpers";
import { fullUrl } from "../components/ImageUpload";
import GuestDocuments from "../components/GuestDocuments";
import { useAuth } from "../AuthContext";
import { Users, Mail, Phone, Upload, FileCheck, Search } from "lucide-react";

export default function Guests() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin", "amministratore"].includes(user?.role);
  const [guests, setGuests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("guests");
  const fileRefs = useRef({});

  const load = () => { api.get("/guests").then((r) => setGuests(r.data)); api.get("/bookings").then((r) => setBookings(r.data)); };
  useEffect(() => { load(); }, []);

  const uploadDoc = async (bookingId, file) => {
    if (!file) return;
    const booking = bookings.find((b) => b.id === bookingId);
    if (!booking) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/bookings/${bookingId}`, { ...booking, document_url: data.url, document_status: "ricevuto" });
      toast.success(t("saved")); load();
    } catch { toast.error("Error"); }
  };

  const filtered = guests.filter((g) =>
    g.guest_name.toLowerCase().includes(q.toLowerCase()) || g.apartment_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6 fade-in" data-testid="guests-page">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
        <button data-testid="guests-tab-guests" onClick={() => setTab("guests")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === "guests" ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>Ospiti</button>
        <button data-testid="guests-tab-documents" onClick={() => setTab("documents")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${tab === "documents" ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100"}`}>Documenti ospiti (GDPR)</button>
      </div>

      {tab === "documents" ? <GuestDocuments isAdmin={isAdmin} /> : (
      <>
      <div className="flex items-center gap-3">
        <div className="relative w-full max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="guests-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchPlaceholder")} className="pl-9" />
        </div>
      </div>

      <Card className="overflow-hidden border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3">{t("guestName")}</th>
                <th className="px-4 py-3">{t("contacts")}</th>
                <th className="px-4 py-3">{t("apartment")}</th>
                <th className="px-4 py-3">{t("stay")}</th>
                <th className="px-4 py-3">{t("present")}</th>
                <th className="px-4 py-3">{t("docStatus")}</th>
                <th className="px-4 py-3">{t("document")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => (
                <tr key={g.booking_id} data-testid={`guest-row-${g.booking_id}`} className="border-b border-slate-100 hover:bg-secondary/30">
                  <td className="px-4 py-3 font-medium text-slate-900">{g.guest_name}<span className="ml-1 text-xs text-slate-400">· {g.guests}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {g.guest_email && <div className="flex items-center gap-1"><Mail size={11} /> {g.guest_email}</div>}
                    {g.guest_phone && <div className="flex items-center gap-1"><Phone size={11} /> {g.guest_phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{g.apartment_name}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(g.check_in)} → {fmtDate(g.check_out)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${g.present ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                      {g.present ? t("present") : t("notPresent")}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge value={g.document_status} /></td>
                  <td className="px-4 py-3">
                    {g.document_url ? (
                      <a href={fullUrl(g.document_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:underline"><FileCheck size={14} /> {t("document")}</a>
                    ) : (
                      <>
                        <input ref={(el) => (fileRefs.current[g.booking_id] = el)} type="file" accept="image/*,.pdf" className="hidden" data-testid={`guest-doc-input-${g.booking_id}`} onChange={(e) => uploadDoc(g.booking_id, e.target.files?.[0])} />
                        <Button data-testid={`guest-doc-upload-${g.booking_id}`} variant="outline" size="sm" className="gap-1" onClick={() => fileRefs.current[g.booking_id]?.click()}><Upload size={13} /> {t("upload")}</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">{t("noData")}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}
    </div>
  );
}
